/* eslint-disable no-nested-ternary */
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Link2,
  Shield,
  Sparkles,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type {
  CoverageSchema,
  DiscriminatedCoverageUpdatedEvent,
} from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

// Type for carrier_link_context (will be in generated types after API regen)
interface CarrierLinkContext {
  carrier_id: string;
  carrier_name: string;
  division_id?: string | null;
  division_name?: string | null;
  reasoning?: string | null;
  match_method?: string | null;
}

interface CoverageUpdatedEventProps {
  event: DiscriminatedCoverageUpdatedEvent;
  matterId: string;
}

export const CoverageUpdatedEvent: React.FC<CoverageUpdatedEventProps> = ({
  event,
  matterId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { payload, occurred_at, user, agent } = event;
  const { coverage, previous } = payload;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const carrierLinkContext = (payload as any)
    .carrier_link_context as CarrierLinkContext | null;

  // If no previous version exists, don't show any change indicators
  const hasPrevious = previous != null;

  // Get current and previous values
  const carrierName = coverage.carrier?.name || 'Unknown Carrier';
  const prevCarrierName = previous?.carrier?.name || 'Unknown Carrier';

  const policyType = coverage.policy_type
    ? formatPolicyType(coverage.policy_type)
    : null;
  const prevPolicyType = previous?.policy_type
    ? formatPolicyType(previous.policy_type)
    : null;

  const category = coverage.category
    ? formatCoverageCategory(coverage.category)
    : null;
  const prevCategory = previous?.category
    ? formatCoverageCategory(previous.category)
    : null;

  const namedInsured = coverage.named_insured?.join(', ');
  const prevNamedInsured = previous?.named_insured?.join(', ');

  const policyNumbers = coverage.policy_identifiers
    ?.map((pi) => pi.identifier_value)
    .filter(Boolean);
  const prevPolicyNumbers = previous?.policy_identifiers
    ?.map((pi) => pi.identifier_value)
    .filter(Boolean);

  // Check what changed (only if previous exists)
  const carrierChanged = hasPrevious && carrierName !== prevCarrierName;
  const policyTypeChanged = hasPrevious && policyType !== prevPolicyType;
  const categoryChanged = hasPrevious && category !== prevCategory;
  const namedInsuredChanged = hasPrevious && namedInsured !== prevNamedInsured;
  const policyNumbersChanged =
    hasPrevious &&
    JSON.stringify(policyNumbers) !== JSON.stringify(prevPolicyNumbers);

  // Check for detailed changes (only if previous exists)
  const benefitsChanged =
    hasPrevious && hasArrayChanged(coverage.benefits, previous?.benefits);
  const adjustersChanged =
    hasPrevious && hasArrayChanged(coverage.adjusters, previous?.adjusters);
  const claimsChanged =
    hasPrevious && hasArrayChanged(coverage.claims, previous?.claims);
  const vehiclesChanged =
    hasPrevious && hasArrayChanged(coverage.vehicles, previous?.vehicles);
  const policyDetailsChanged =
    hasPrevious &&
    hasArrayChanged(coverage.policy_details, previous?.policy_details);

  const hasDetailedChanges =
    benefitsChanged ||
    adjustersChanged ||
    claimsChanged ||
    vehiclesChanged ||
    policyDetailsChanged;

  const statusChanged = hasPrevious && coverage.status !== previous?.status;

  return (
    <EventContainer>
      <EventHeader
        icon={<Shield size={20} />}
        title={
          carrierLinkContext ? 'Coverage Linked to Carrier' : 'Coverage Updated'
        }
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Carrier Link Context - show when agent linked coverage to carrier */}
          {carrierLinkContext && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mb-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-blue-800 mb-1">
                <Link2 size={12} />
                <span>Linked to Carrier Directory</span>
              </div>
              <div className="text-xs space-y-0.5">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600">Carrier:</span>
                  <span className="font-medium">
                    {carrierLinkContext.carrier_name}
                  </span>
                </div>
                {carrierLinkContext.division_name && (
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600">Division:</span>
                    <span>{carrierLinkContext.division_name}</span>
                  </div>
                )}
                {carrierLinkContext.match_method && (
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600">Method:</span>
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      {formatMatchMethod(carrierLinkContext.match_method)}
                    </Badge>
                  </div>
                )}
                {carrierLinkContext.reasoning && (
                  <div className="mt-1.5 pt-1.5 border-t border-blue-200">
                    <div className="flex items-start gap-1">
                      <Sparkles
                        size={11}
                        className="text-blue-600 mt-0.5 shrink-0"
                      />
                      <span className="text-slate-700 italic text-[11px]">
                        {carrierLinkContext.reasoning}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Carrier and Policy Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {carrierChanged ? (
              <div className="flex items-center gap-1.5">
                <span className="font-medium line-through text-slate-400">
                  {prevCarrierName}
                </span>
                <span className="text-slate-400">→</span>
                <span className="font-medium text-green-700">
                  {carrierName}
                </span>
              </div>
            ) : (
              <span className="font-medium">{carrierName}</span>
            )}

            {policyType && (
              <>
                <span className="text-slate-400">•</span>
                {policyTypeChanged ? (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="line-through">
                      {prevPolicyType}
                    </Badge>
                    <span className="text-slate-400">→</span>
                    <Badge variant="outline" className="border-green-600">
                      {policyType}
                    </Badge>
                  </div>
                ) : (
                  <Badge variant="outline">{policyType}</Badge>
                )}
              </>
            )}

            {category && (
              <>
                <span className="text-slate-400">•</span>
                {categoryChanged ? (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="line-through">
                      {prevCategory}
                    </Badge>
                    <span className="text-slate-400">→</span>
                    <Badge variant="outline" className="border-green-600">
                      {category}
                    </Badge>
                  </div>
                ) : (
                  <Badge variant="outline">{category}</Badge>
                )}
              </>
            )}

            {statusChanged && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline" className="border-green-600">
                  {formatStatus(coverage.status)}
                </Badge>
              </>
            )}
          </div>

          {/* Named Insured */}
          {(namedInsured || prevNamedInsured) && (
            <div className="text-xs">
              <span className="text-slate-600">Insured:</span>{' '}
              {namedInsuredChanged ? (
                <span>
                  {prevNamedInsured && (
                    <span className="line-through text-slate-400">
                      {prevNamedInsured}
                    </span>
                  )}
                  {prevNamedInsured && namedInsured && (
                    <span className="text-slate-400"> → </span>
                  )}
                  {namedInsured && (
                    <span className="text-green-700">{namedInsured}</span>
                  )}
                </span>
              ) : (
                <span>{namedInsured}</span>
              )}
            </div>
          )}

          {/* Policy Numbers */}
          {(policyNumbers && policyNumbers.length > 0) ||
          (prevPolicyNumbers && prevPolicyNumbers.length > 0) ? (
            <div className="text-xs">
              <span className="text-slate-600">Policy Numbers:</span>{' '}
              {policyNumbersChanged ? (
                <span className="font-mono text-[11px]">
                  {prevPolicyNumbers && prevPolicyNumbers.length > 0 && (
                    <span className="line-through text-slate-400">
                      {prevPolicyNumbers.join(', ')}
                    </span>
                  )}
                  {prevPolicyNumbers &&
                    prevPolicyNumbers.length > 0 &&
                    policyNumbers &&
                    policyNumbers.length > 0 && (
                      <span className="text-slate-400"> → </span>
                    )}
                  {policyNumbers && policyNumbers.length > 0 && (
                    <span className="text-green-700">
                      {policyNumbers.join(', ')}
                    </span>
                  )}
                </span>
              ) : (
                <span className="font-mono text-[11px]">
                  {policyNumbers?.join(', ')}
                </span>
              )}
            </div>
          ) : null}

          {/* Benefits - always show current if exists */}
          {coverage.benefits && coverage.benefits.length > 0 && (
            <div className="text-xs">
              <span className="text-slate-600">Benefits:</span>{' '}
              <span className="text-[11px]">
                {coverage.benefits
                  .map((b) => {
                    const label = formatBenefitType(b.benefit_type);
                    if (b.limitations) {
                      const values = Object.values(b.limitations);
                      if (values.length > 0) {
                        return `${label} (${values.map(formatLimitCompact).join('/')})`;
                      }
                    }
                    return label;
                  })
                  .join(', ')}
              </span>
              {benefitsChanged && (
                <span className="text-green-700 ml-1">(updated)</span>
              )}
            </div>
          )}

          {/* Collapsible Changes Details */}
          {hasDetailedChanges && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-1.5">
                {isOpen ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span>{isOpen ? 'Hide' : 'Show'} changes</span>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-2 space-y-2.5 border-l-2 border-slate-200 pl-2.5">
                {/* Benefits Changes */}
                {benefitsChanged && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Benefits Changes
                    </div>
                    {renderBenefitsChanges(
                      coverage.benefits,
                      previous?.benefits,
                    )}
                  </div>
                )}

                {/* Adjusters Changes */}
                {adjustersChanged && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Adjusters Changes
                    </div>
                    {renderAdjustersChanges(
                      coverage.adjusters,
                      previous?.adjusters,
                    )}
                  </div>
                )}

                {/* Claims Changes */}
                {claimsChanged && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Claims Changes
                    </div>
                    {renderClaimsChanges(coverage.claims, previous?.claims)}
                  </div>
                )}

                {/* Vehicles Changes */}
                {vehiclesChanged && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Vehicles Changes
                    </div>
                    {renderVehiclesChanges(
                      coverage.vehicles,
                      previous?.vehicles,
                    )}
                  </div>
                )}

                {/* Policy Details Changes */}
                {policyDetailsChanged && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Policy Details Changes
                    </div>
                    {renderPolicyDetailsChanges(
                      coverage.policy_details,
                      previous?.policy_details,
                    )}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/coverage/${coverage.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Coverage
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions
function hasArrayChanged(
  current: any[] | null | undefined,
  previous: any[] | null | undefined,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(previous);
}

function formatPolicyType(type: string): string {
  const types: Record<string, string> = {
    AUTO: 'Auto',
    HOMEOWNERS: 'Homeowners',
    COMMERCIAL: 'Commercial',
    HEALTH: 'Health',
    UMBRELLA: 'Umbrella',
    OTHER: 'Other',
  };
  return types[type] || type;
}

function formatCoverageCategory(category: string): string {
  const categories: Record<string, string> = {
    FIRST_PARTY: '1st Party',
    FIRST_PARTY_HEALTH: '1st Party Health',
    THIRD_PARTY: '3rd Party',
    UNCLEAR: 'Unclear',
  };
  return categories[category] || category;
}

function formatStatus(status: string | null | undefined): string {
  if (!status) return '';
  const statuses: Record<string, string> = {
    NEEDS_APPROVAL: 'Needs Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };
  return statuses[status] || status;
}

function formatBenefitType(type: string | null | undefined): string {
  if (!type) return '';

  const benefits: Record<string, string> = {
    PERSONAL_INJURY_PROTECTION: 'PIP',
    MEDICAL_PAYMENTS: 'Med Pay',
    UNINSURED_MOTORIST: 'UM',
    UNDER_INSURED_MOTORIST: 'UIM',
    BODILY_INJURY_LIABILITY: 'BI',
    PROPERTY_DAMAGE_LIABILITY: 'PD',
    COMPREHENSIVE_AUTO: 'Comprehensive',
    COLLISION_AUTO: 'Collision',
    OTHER: 'Other',
  };
  return benefits[type] || type;
}

function formatMatchMethod(method: string): string {
  const methods: Record<string, string> = {
    policy_pattern: 'Policy Pattern',
    name_match: 'Name Match',
    manual: 'Manual',
    fuzzy_match: 'Fuzzy Match',
  };
  return methods[method] || method;
}

function formatLimitCompact(value: number): string {
  if (value >= 1000000) {
    const num = value / 1000000;
    return `$${num % 1 === 0 ? num.toFixed(0) : parseFloat(num.toFixed(2))}M`;
  }
  if (value >= 1000) {
    const num = value / 1000;
    return `$${num % 1 === 0 ? num.toFixed(0) : parseFloat(num.toFixed(2))}K`;
  }
  return `$${value.toLocaleString()}`;
}

// Render change functions
function renderBenefitsChanges(
  current: CoverageSchema['benefits'],
  previous: CoverageSchema['benefits'],
) {
  const currentBenefits = current || [];
  const previousBenefits = previous || [];

  const prevMap = new Map(
    previousBenefits.map((b) => [b.benefit_type, b]),
  );
  const currMap = new Map(
    currentBenefits.map((b) => [b.benefit_type, b]),
  );

  const formatLimits = (
    limitations: Record<string, number> | null | undefined,
  ): string => {
    if (!limitations) return 'No limits';
    const entries = Object.entries(limitations);
    if (entries.length === 0) return 'No limits';
    return entries.map(([, val]) => formatLimitCompact(val)).join(' / ');
  };

  return (
    <div className="space-y-1">
      {currentBenefits.map((benefit, index) => {
        const prevBenefit = prevMap.get(benefit.benefit_type);
        const isNew = !prevBenefit;
        const limitsChanged =
          prevBenefit &&
          JSON.stringify(prevBenefit.limitations) !==
            JSON.stringify(benefit.limitations);

        return (
          <div key={index} className="text-[11px]">
            <div className="flex items-center gap-1.5">
              <span className="font-medium min-w-[60px]">
                {formatBenefitType(benefit.benefit_type)}
              </span>
              {isNew ? (
                <span className="text-green-700">
                  + {formatLimits(benefit.limitations)}
                </span>
              ) : limitsChanged ? (
                <span>
                  <span className="line-through text-slate-400">
                    {formatLimits(prevBenefit.limitations)}
                  </span>
                  <span className="text-slate-400"> → </span>
                  <span className="text-green-700">
                    {formatLimits(benefit.limitations)}
                  </span>
                </span>
              ) : (
                <span className="text-slate-600">
                  {formatLimits(benefit.limitations)}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {previousBenefits
        .filter((b) => !currMap.has(b.benefit_type))
        .map((benefit, index) => (
          <div key={`removed-${index}`} className="text-[11px]">
            <div className="flex items-center gap-1.5">
              <span className="font-medium min-w-[60px]">
                {formatBenefitType(benefit.benefit_type)}
              </span>
              <span className="text-red-700 line-through">
                {formatLimits(benefit.limitations)}
              </span>
            </div>
          </div>
        ))}
    </div>
  );
}

function renderAdjustersChanges(
  current: CoverageSchema['adjusters'],
  previous: CoverageSchema['adjusters'],
) {
  const currentAdjusters = current || [];
  const previousAdjusters = previous || [];

  return (
    <div className="space-y-1">
      {currentAdjusters.map((adjuster, index) => {
        const isNew = index >= previousAdjusters.length;
        return (
          <div
            key={index}
            className={`text-[11px] ${isNew ? 'text-green-700' : ''}`}
          >
            {isNew && <span className="mr-1">+</span>}
            {adjuster.name || 'Unnamed adjuster'}
            {adjuster.benefit_type && (
              <span className="text-slate-600">
                {' '}
                ({formatBenefitType(adjuster.benefit_type)})
              </span>
            )}
          </div>
        );
      })}
      {previousAdjusters.length > currentAdjusters.length && (
        <div className="text-[11px] text-red-700">
          - Removed {previousAdjusters.length - currentAdjusters.length}{' '}
          adjuster(s)
        </div>
      )}
    </div>
  );
}

function renderClaimsChanges(
  current: CoverageSchema['claims'],
  previous: CoverageSchema['claims'],
) {
  const currentClaims = current || [];
  const previousClaims = previous || [];

  return (
    <div className="space-y-1">
      {currentClaims.map((claim, index) => {
        const isNew = index >= previousClaims.length;
        return (
          <div
            key={index}
            className={`text-[11px] ${isNew ? 'text-green-700' : ''}`}
          >
            {isNew && <span className="mr-1">+</span>}
            Claim {claim.claim_identifier || 'No ID'}
            {claim.benefit_type && (
              <span className="text-slate-600">
                {' '}
                ({formatBenefitType(claim.benefit_type)})
              </span>
            )}
          </div>
        );
      })}
      {previousClaims.length > currentClaims.length && (
        <div className="text-[11px] text-red-700">
          - Removed {previousClaims.length - currentClaims.length} claim(s)
        </div>
      )}
    </div>
  );
}

function renderVehiclesChanges(
  current: CoverageSchema['vehicles'],
  previous: CoverageSchema['vehicles'],
) {
  const currentVehicles = current || [];
  const previousVehicles = previous || [];

  return (
    <div className="space-y-1">
      {currentVehicles.map((vehicle, index) => {
        const isNew = index >= previousVehicles.length;
        const vehicleDesc =
          [vehicle.year, vehicle.make, vehicle.model]
            .filter(Boolean)
            .join(' ') || 'Vehicle';
        return (
          <div
            key={index}
            className={`text-[11px] ${isNew ? 'text-green-700' : ''}`}
          >
            {isNew && <span className="mr-1">+</span>}
            {vehicleDesc}
            {vehicle.vin && (
              <span className="text-slate-600 font-mono"> ({vehicle.vin})</span>
            )}
          </div>
        );
      })}
      {previousVehicles.length > currentVehicles.length && (
        <div className="text-[11px] text-red-700">
          - Removed {previousVehicles.length - currentVehicles.length}{' '}
          vehicle(s)
        </div>
      )}
    </div>
  );
}

function renderPolicyDetailsChanges(
  current: CoverageSchema['policy_details'],
  previous: CoverageSchema['policy_details'],
) {
  const currentDetails = current || [];
  const previousDetails = previous || [];

  // Create maps for easier comparison
  const prevMap = new Map(previousDetails.map((d) => [d.key, d.value]));
  const currMap = new Map(currentDetails.map((d) => [d.key, d.value]));

  return (
    <div className="space-y-0.5">
      {currentDetails.map((detail, index) => {
        const prevValue = prevMap.get(detail.key);
        const isNew = !prevMap.has(detail.key);
        const isModified = prevValue && prevValue !== detail.value;

        return (
          <div key={index} className="text-[11px] flex gap-2">
            <span className="text-slate-600 min-w-[100px]">{detail.key}:</span>
            {isNew ? (
              <span className="flex-1 text-green-700">+ {detail.value}</span>
            ) : isModified ? (
              <span className="flex-1">
                <span className="line-through text-slate-400">{prevValue}</span>
                <span className="text-slate-400"> → </span>
                <span className="text-green-700">{detail.value}</span>
              </span>
            ) : (
              <span className="flex-1">{detail.value}</span>
            )}
          </div>
        );
      })}
      {/* Show removed details */}
      {previousDetails
        .filter((d) => !currMap.has(d.key))
        .map((detail, index) => (
          <div key={`removed-${index}`} className="text-[11px] flex gap-2">
            <span className="text-slate-600 min-w-[100px]">{detail.key}:</span>
            <span className="flex-1 text-red-700 line-through">
              {detail.value}
            </span>
          </div>
        ))}
    </div>
  );
}
